import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [form, setForm] = useState({ password: '', confirm_password: '' });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('No reset token found. Please request a new password reset link.');
        }
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(''); setSuccess(''); setLoading(true);
        if (form.password.length < 8) { setError('Password must be at least 8 characters'); setLoading(false); return; }
        if (form.password !== form.confirm_password) { setError('Passwords do not match'); setLoading(false); return; }
        try {
            const r = await axios.post(`${API}/auth/reset-password`, { token, ...form });
            setSuccess(r.data.message);
        } catch(err) {
            setError(err.response?.data?.detail || 'This reset link has expired or already been used. Please request a new one.');
        } finally { setLoading(false); }
    };

    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent";
    const labelCls = "font-body text-navy/70 text-sm font-semibold block mb-1";

    return (
        <div className="min-h-screen bg-ivory flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-lg max-w-md w-full p-10">
                <div className="text-center mb-8">
                    <h1 className="font-heading text-3xl text-navy font-bold">KNOW YOUR STUFF</h1>
                    <p className="font-body text-navy/60 mt-2">Reset Your Password</p>
                </div>

                {success ? (
                    <div className="text-center space-y-4" data-testid="reset-success">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
                            </svg>
                        </div>
                        <p className="font-body text-navy/80 text-lg">{success}</p>
                        <button onClick={()=>navigate('/')} className="bg-gold hover:bg-gold-hover text-navy font-bold px-8 py-3 rounded-sm font-body transition-colors min-h-[44px]" data-testid="back-to-login-btn">
                            Back to Login
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
                        <div>
                            <label className={labelCls}>New Password</label>
                            <input type="password" required value={form.password} onChange={e=>setForm({...form,password:e.target.value})} className={inputCls} placeholder="Minimum 8 characters" data-testid="new-password-input"/>
                        </div>
                        <div>
                            <label className={labelCls}>Confirm New Password</label>
                            <input type="password" required value={form.confirm_password} onChange={e=>setForm({...form,confirm_password:e.target.value})} className={inputCls} data-testid="confirm-password-input"/>
                        </div>
                        {error && (
                            <div className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3" data-testid="reset-error">
                                <p>{error}</p>
                                {error.includes('expired') && (
                                    <button type="button" onClick={()=>navigate('/#account')} className="text-gold hover:underline text-sm mt-2 block">
                                        Request a new reset link
                                    </button>
                                )}
                            </div>
                        )}
                        <button type="submit" disabled={loading || !token} className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]" data-testid="reset-submit-btn">
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>
                        <div className="text-center">
                            <button type="button" onClick={()=>navigate('/')} className="font-body text-gold hover:underline text-sm" data-testid="back-to-home-btn">Back to Home</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
